import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BellRing,
  CalendarCheck,
  CheckCircle2,
  CreditCard,
  DollarSign,
  Home,
  Lock,
  Plane,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Wallet,
} from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: { staggerChildren: 0.1 },
  },
};

const capabilityGroups = [
  {
    icon: <CreditCard className="w-5 h-5" />,
    title: "Money and bills",
    description: "Pay bills, track due dates, monitor recurring charges, and keep routine payments moving.",
    prompts: ["Pay my hydro bill.", "What subscriptions am I paying for?"],
  },
  {
    icon: <Plane className="w-5 h-5" />,
    title: "Travel planning",
    description: "Search flights and hotels, compare options, and help organize trips around your preferences.",
    prompts: ["Find me a hotel in Rome under $200.", "Plan my weekend trip."],
  },
  {
    icon: <CalendarCheck className="w-5 h-5" />,
    title: "Calendar and reminders",
    description: "Schedule tasks, coordinate plans, and keep important commitments from falling through the cracks.",
    prompts: ["Remind me to call the dentist tomorrow at 11 am.", "Move dinner to Friday night."],
  },
  {
    icon: <ShoppingBag className="w-5 h-5" />,
    title: "Shopping and deals",
    description: "Compare prices, surface better options, and help you buy with less searching and fewer tabs.",
    prompts: ["Find the best deal on noise-cancelling headphones.", "Reorder my essentials."],
  },
  {
    icon: <Home className="w-5 h-5" />,
    title: "Home admin",
    description: "Handle routine life maintenance like appointments, recurring errands, and household to-dos.",
    prompts: ["Book a furnace service reminder.", "Help me organize next week."],
  },
  {
    icon: <Wallet className="w-5 h-5" />,
    title: "Personal operations",
    description: "Josh is being built to become a single operating layer for the tasks that usually live across many apps.",
    prompts: ["Handle my monthly admin.", "What do I need to take care of today?"],
  },
];

const productPillars = [
  {
    title: "Conversation first",
    description: "No hunting through interfaces. You ask naturally and Josh figures out the workflow.",
  },
  {
    title: "Connected actions",
    description: "Josh is being built to work with the services and accounts you already rely on.",
  },
  {
    title: "Approval before action",
    description: "For important tasks, Josh confirms what will happen before it happens.",
  },
  {
    title: "Memory that helps",
    description: "Preferences, recurring tasks, and personal context make Josh more useful over time.",
  },
];

const steps = [
  {
    number: "01",
    title: "You ask",
    description: "Type or speak like you would to a capable assistant.",
    example: "“Josh, pay my internet bill and remind me when rent is due.”",
  },
  {
    number: "02",
    title: "Josh prepares the work",
    description: "Josh gathers the right information, checks your saved context, and builds the task flow.",
    example: "Accounts, preferences, due dates, services, and required approvals.",
  },
  {
    number: "03",
    title: "Josh gets it done",
    description: "Josh completes the action, keeps you updated, and reduces the number of apps you need to touch.",
    example: "Less switching, less admin, less mental overhead.",
  },
];

const scenarios = [
  {
    title: "Morning",
    text: "“Josh, what do I need to take care of today?”",
  },
  {
    title: "Midday",
    text: "“Josh, pay the credit card bill and move my haircut to next week.”",
  },
  {
    title: "Evening",
    text: "“Josh, find me the best weekend getaway within driving distance.”",
  },
  {
    title: "Monthly",
    text: "“Josh, review my subscriptions and flag anything I should cancel.”",
  },
];

const differentiators = [
  "Most assistants stop at answers. Josh is being built to complete the task.",
  "Josh aims to replace app switching with one clear conversation.",
  "Josh is designed for real-life operations, not one-off novelty prompts.",
  "The goal is a trusted personal AI that saves time every day.",
];

const trustPoints = [
  {
    icon: <Lock className="w-5 h-5" />,
    title: "Secure by design",
    description: "Protected infrastructure and secure handling are foundational, not optional.",
  },
  {
    icon: <ShieldCheck className="w-5 h-5" />,
    title: "You approve sensitive actions",
    description: "Josh only acts on your behalf when the right confirmation and permission are in place.",
  },
  {
    icon: <BellRing className="w-5 h-5" />,
    title: "Clear visibility",
    description: "Josh is intended to keep actions understandable, reviewable, and under your control.",
  },
];

const JoshPage = () => {
  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="josh-page">
      <section className="min-h-[calc(100vh-5rem)] flex items-center py-16 md:py-24">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center"
          >
            <div className="max-w-4xl">
              <p className="caption mb-4">Josh AI</p>
              <h1 className="heading-1 mb-6">
                One AI for {" "}
                <span className="bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">
                  life&apos;s moving parts.
                </span>
              </h1>
              <p className="body-lg max-w-3xl mb-6">
                Josh is being built as a personal AI that handles the tasks, errands,
                planning, and follow-through that usually get scattered across dozens of apps.
              </p>
              <p className="body-text max-w-2xl mb-10">
                Bills, reminders, travel, subscriptions, schedules, shopping, and
                everyday admin should feel like one conversation, not a pile of open tabs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="josh-hero-cta"
                >
                  Join Early Access
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  className="btn-secondary inline-flex items-center justify-center"
                  data-testid="josh-hero-secondary-cta"
                >
                  Talk to The App Fam
                </Link>
              </div>
            </div>

            <motion.div
              {...fadeInUp}
              className="relative rounded-[2.5rem] bg-[#F5F5F7] border border-zinc-100 p-8 md:p-10 overflow-hidden"
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(217,70,239,0.14),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(147,51,234,0.1),transparent_30%)]" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm">
                  <Sparkles className="w-4 h-4 text-fuchsia-500" />
                  Personal AI, built for action
                </div>
                <div className="mt-8 space-y-4">
                  {[
                    "Pay my hydro bill and tell me what’s due this week.",
                    "Find the best hotel in Rome under $200 and shortlist three.",
                    "What subscriptions am I paying for that I haven’t used lately?",
                    "Build me a grocery list for dinners this week and find the best prices nearby.",
                    "Plan my weekend and remind me what I need to book.",
                  ].map((item) => (
                    <div
                      key={item}
                      className="rounded-2xl bg-white p-4 text-sm md:text-base text-zinc-700 shadow-[0_10px_30px_rgba(0,0,0,0.04)]"
                    >
                      {item}
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-sm text-zinc-500">
                  One assistant. Real workflows. Less overhead.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">Capability Map</p>
            <h2 className="heading-2 mb-6">What Josh is building toward.</h2>
            <p className="body-text">
              Josh is designed to become the operating layer for everyday life
              management, combining planning, execution, memory, and follow-through.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            {capabilityGroups.map((item) => (
              <motion.div key={item.title} variants={fadeInUp} className="feature-card">
                <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 mb-5">
                  {item.icon}
                </div>
                <h3 className="text-xl font-medium text-zinc-900 mb-3">{item.title}</h3>
                <p className="text-sm leading-relaxed text-zinc-600 mb-5">{item.description}</p>
                <div className="space-y-2">
                  {item.prompts.map((prompt) => (
                    <div key={prompt} className="rounded-2xl bg-[#F5F5F7] px-4 py-3 text-sm text-zinc-600">
                      {prompt}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div {...fadeInUp}>
              <p className="caption mb-4">Why Josh</p>
              <h2 className="heading-2 mb-6">Built for execution, not just conversation.</h2>
              <p className="body-text mb-8">
                The long-term value of Josh is not simply answering questions. It is
                reducing friction between intent and action so everyday responsibilities
                feel lighter.
              </p>
              <div className="space-y-4">
                {differentiators.map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 mt-1 text-zinc-500 flex-shrink-0" />
                    <p className="body-text">{point}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="feature-card">
              <p className="caption mb-4">Core Product Principles</p>
              <h3 className="heading-3 mb-6">How Josh should feel.</h3>
              <div className="space-y-5">
                {productPillars.map((pillar) => (
                  <div key={pillar.title}>
                    <p className="font-medium text-zinc-900">{pillar.title}</p>
                    <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{pillar.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">How It Works</p>
            <h2 className="heading-2 mb-6">Simple on the surface. Powerful underneath.</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            {steps.map((step) => (
              <motion.div key={step.number} variants={fadeInUp} className="feature-card">
                <p className="text-sm font-semibold tracking-[0.2em] text-zinc-400 mb-5">{step.number}</p>
                <h3 className="heading-3 mb-4">{step.title}</h3>
                <p className="body-text mb-5">{step.description}</p>
                <div className="rounded-2xl bg-[#F5F5F7] px-5 py-4 text-sm text-zinc-600">
                  {step.example}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">Daily Scenarios</p>
            <h2 className="heading-2 mb-6">What using Josh could feel like.</h2>
            <p className="body-text">
              Josh is being built to help across the day, not just in isolated moments.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6"
          >
            {scenarios.map((scenario) => (
              <motion.div key={scenario.title} variants={fadeInUp} className="feature-card">
                <p className="caption mb-4">{scenario.title}</p>
                <p className="text-lg font-medium tracking-tight text-zinc-900 leading-relaxed">
                  {scenario.text}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div {...fadeInUp}>
              <p className="caption mb-4">The Vision</p>
              <h2 className="heading-2 mb-6">Less app chaos. More done.</h2>
              <p className="body-text mb-8">
                The vision for Josh is a future where you no longer manage life through
                a patchwork of disconnected tools. You simply say what needs to happen,
                and your assistant handles the operational work around it.
              </p>
              <div className="rounded-[1.5rem] bg-white p-6 border border-zinc-100">
                <p className="text-sm uppercase tracking-[0.2em] text-zinc-400 mb-3">
                  The idea in one line
                </p>
                <p className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-900">
                  “Josh, handle it.”
                </p>
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="feature-card">
              <p className="caption mb-4">Save Time Every Week</p>
              <h3 className="heading-3 mb-4">Admin work should not dominate your attention.</h3>
              <div className="space-y-4 text-zinc-600">
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 mt-1 text-zinc-500 flex-shrink-0" />
                  <p className="text-sm leading-relaxed">
                    Reduce repetitive tasks like bill management, recurring reminders, and subscription review.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CalendarCheck className="w-5 h-5 mt-1 text-zinc-500 flex-shrink-0" />
                  <p className="text-sm leading-relaxed">
                    Keep plans, appointments, and life logistics coordinated in one place.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <ShoppingBag className="w-5 h-5 mt-1 text-zinc-500 flex-shrink-0" />
                  <p className="text-sm leading-relaxed">
                    Cut down research time across shopping, travel, and everyday decision-making.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">Privacy and Control</p>
            <h2 className="heading-2 mb-6">A useful assistant must also be a trusted one.</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {trustPoints.map((point) => (
              <motion.div key={point.title} variants={fadeInUp} className="feature-card text-center">
                <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-5 text-zinc-600">
                  {point.icon}
                </div>
                <h3 className="text-xl font-medium text-zinc-900 mb-3">{point.title}</h3>
                <p className="text-sm leading-relaxed text-zinc-600">{point.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-zinc-900">
        <div className="container-main text-center">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto">
            <p className="caption text-zinc-500 mb-4">Early Access</p>
            <h2 className="heading-2 text-white mb-6">Help shape Josh from the beginning.</h2>
            <p className="text-lg text-zinc-400 leading-relaxed mb-10">
              Josh is in early development. Join the early access list if you want to
              follow the product, give feedback, and be among the first to try a more
              action-oriented personal AI from The App Fam.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="h-12 px-8 rounded-full bg-white text-zinc-900 font-medium text-sm hover:bg-zinc-100 transition-all inline-flex items-center justify-center gap-2"
                data-testid="josh-early-access-cta"
              >
                Get Early Access
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/apps"
                className="h-12 px-8 rounded-full border border-zinc-700 text-white font-medium text-sm hover:bg-zinc-800 transition-all inline-flex items-center justify-center"
                data-testid="josh-apps-cta"
              >
                Explore The App Fam
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default JoshPage;
